import {createSlice} from '@reduxjs/toolkit';
import {getOpenInspectionReportIds} from './shopviewCommon';

const shopviewSlice = createSlice({
  name: 'shopview',
  initialState: {
    loading: true,
    vehiclesLookUp: {},
    serviceTasks: [],
    odoByVehiclesnUnitsKm: {},
    engHrByVehiclesnUnitsHrs: {},
    inspectionItemsLookUp: {},
    breakdownReportsLookUp: {},
    maintenanceItems: [],
    maintenanceSummary: {},
    historyItems: [],
    linkedInspectionReportsById: {},
    vehicleLabels: [],
    implementLabelsDict: {},
    serviceChecklistItems: {},
    lastChecklistItemStatusByVehicle: {},
    lastChecklistItemStatusByImplement: {},
    addHistoryModalOpen: false,
    useCompleteTaskDefaultValues: false,
    // Implement-related state
    implements: {},
    implementHoursByImplementSn: {},
  },
  reducers: {
    updateLoading(state, action) {
      state.loading = action.payload;
    },
    updateLinkedInspectionReports(state, action) {
      state.linkedInspectionReportsById = action.payload;
    },
    updateShopviewData(state, action) {
      // Vehicle lookup
      const vehicles = action.payload.vehicles;
      // Create vehicles lookup based on vehicle serialNumber. Key: serialNumber, Value: vehicle document
      const vehiclesSerialLookUp = {};
      for (let i = 0; i < vehicles.length; i++) {
        const vehicle = vehicles[i];

        if (typeof vehicle.serialNumber === 'undefined') {
          console.error('getVehiclesSerialLookUp vehicle does not have a serialNumber: ', vehicle);
          continue;
        }

        // Append service entry to lookup
        if (!vehiclesSerialLookUp.hasOwnProperty(vehicle.serialNumber)) {
          vehiclesSerialLookUp[vehicle.serialNumber] = vehicle;
        }
      }

      state.vehiclesLookUp = vehiclesSerialLookUp;

      // Service tasks
      state.serviceTasks = action.payload.serviceTasks;

      // Inspection Items
      const inspectionItems = action.payload.inspectionItems;

      // Create service entries lookup based on service inspection item ID. Key: id, Value: data
      const inspectionItemsLookUp = {};
      for (let i = 0; i < inspectionItems.length; i++) {
        const inspectionItem = inspectionItems[i];
        // Append service entry to lookup
        if (!inspectionItemsLookUp.hasOwnProperty(inspectionItem.id)) {
          inspectionItemsLookUp[inspectionItem.id] = inspectionItem;
        }
      }

      state.inspectionItemsLookUp = inspectionItemsLookUp;

      const breakdownReports = action.payload.breakdownReports || [];
      const breakdownReportsLookUp = {};
      for (let i = 0; i < breakdownReports.length; i++) {
        const breakdownReport = breakdownReports[i];
        if (!breakdownReportsLookUp.hasOwnProperty(breakdownReport.id)) {
          breakdownReportsLookUp[breakdownReport.id] = breakdownReport;
        }
      }
      state.breakdownReportsLookUp = breakdownReportsLookUp;

      state.vehicleLabels = action.payload.vehicleLabels;
      state.serviceChecklistItems = action.payload.serviceChecklistItems;

      // Build implement labels dict
      const implementLabels = action.payload.implementLabels || [];
      const implementLabelsDict = {};
      implementLabels.forEach((label) => {
        implementLabelsDict[label.id] = label;
      });
      state.implementLabelsDict = implementLabelsDict;
    },
    updateMaintenanceData(state, action) {
      const maintenanceData = action.payload;
      state.maintenanceItems = maintenanceData.maintenanceItems;
      state.maintenanceSummary = maintenanceData.maintenanceSummary;
      // Normalize historyItems so each serviceEntry has date_time for ordering (prefer over date)
      state.historyItems = (maintenanceData.historyItems || []).map((item) => {
        const se = item.serviceEntry;
        if (!se) return item;
        const dateTime = se.date_time ?? se.datetime ?? se.date;
        return {
          ...item,
          serviceEntry: {...se, date_time: dateTime, dateTime: dateTime ?? se.date},
        };
      });
      state.odoByVehiclesnUnitsKm = maintenanceData.odoByVehiclesnUnitsKm;
      state.engHrByVehiclesnUnitsHrs = maintenanceData.engHrByVehiclesnUnitsHrs;
      state.implementHoursByImplementSn = maintenanceData.implementHoursByImplementSn;
      state.lastChecklistItemStatusByVehicle = maintenanceData.lastChecklistItemStatusByVehicle;
      state.lastChecklistItemStatusByImplement = maintenanceData.lastChecklistItemStatusByImplement || {};
    },
    updateAddHistoryModalOpen(state, action) {
      state.addHistoryModalOpen = action.payload;
    },
    updateUseCompleteTaskDefaultValues(state, action) {
      state.useCompleteTaskDefaultValues = action.payload;
    },
    updateImplementData(state, action) {
      state.implements = action.payload.implements || {};
    },
    updateAlertviewLookups(state, action) {
      const {vehicles, inspectionItems, implements: implementsData, breakdownReports} = action.payload;

      if (vehicles) {
        const vehiclesSerialLookUp = {};
        for (let i = 0; i < vehicles.length; i++) {
          const vehicle = vehicles[i];
          if (typeof vehicle.serialNumber === 'undefined') continue;
          if (!vehiclesSerialLookUp.hasOwnProperty(vehicle.serialNumber)) {
            vehiclesSerialLookUp[vehicle.serialNumber] = vehicle;
          }
        }
        state.vehiclesLookUp = vehiclesSerialLookUp;
      }

      if (inspectionItems) {
        const inspectionItemsLookUp = {};
        for (let i = 0; i < inspectionItems.length; i++) {
          const inspectionItem = inspectionItems[i];
          if (!inspectionItemsLookUp.hasOwnProperty(inspectionItem.id)) {
            inspectionItemsLookUp[inspectionItem.id] = inspectionItem;
          }
        }
        state.inspectionItemsLookUp = inspectionItemsLookUp;
      }

      if (implementsData) {
        state.implements = implementsData;
      }

      if (Array.isArray(breakdownReports) && breakdownReports.length > 0) {
        const nextLookup = {...state.breakdownReportsLookUp};
        for (let i = 0; i < breakdownReports.length; i++) {
          const breakdownReport = breakdownReports[i];
          const reportId = breakdownReport?.id || breakdownReport?.reportDocId;
          if (reportId) {
            nextLookup[reportId] = {
              ...breakdownReport,
              id: reportId,
            };
          }
        }
        state.breakdownReportsLookUp = nextLookup;
      }
    },
    /**
     * Merge a newly created/updated active service entry into maintenanceItems so the Upcoming table
     * updates immediately without waiting for the maintenance report to refetch.
     * Either updates an existing item's activeServiceEntry or appends a synthetic row.
     * @param {Object} state - Current shopview slice state
     * @param {Object} action - payload: entry, vehicleName, implementName, vehicleSN, implementSN, isImplementService, serviceTask
     */
    mergeActiveServiceEntry(state, action) {
      const {entry, vehicleName, implementName, vehicleSN, implementSN, isImplementService, serviceTask} =
        action.payload;
      const taskId = entry?.service_task_id;
      const targetSN = entry
        ? isImplementService
          ? entry.implement_sn || implementSN
          : entry.vehicle_sn || vehicleSN
        : '';

      if (!entry || !serviceTask) return;
      if (!targetSN) return;

      const idx = state.maintenanceItems.findIndex((item) => {
        const itemSN = isImplementService ? item.implement_sn : item.vehicle_sn;
        const itemTaskId = item.serviceTask?.id ?? item.serviceTask?.data?.id;
        return itemSN === targetSN && (itemTaskId == taskId || itemTaskId === taskId); // eslint-disable-line eqeqeq
      });

      const syntheticRow = {
        serviceTask: serviceTask.data ? {...serviceTask.data} : serviceTask,
        vehicle_sn: isImplementService ? '' : targetSN,
        vehicleName: isImplementService ? '' : vehicleName || targetSN,
        implement_sn: isImplementService ? targetSN : '',
        implementName: isImplementService ? implementName || targetSN : '',
        activeServiceEntry: entry,
        overdue: false,
        upcoming: true,
        inspectionFailure: Boolean(
          (serviceTask.data ? serviceTask.data : serviceTask)?.inspection_failure ||
            (serviceTask.data ? serviceTask.data : serviceTask)?.inspectionFailure
        ),
        breakdownReport: Boolean(
          (serviceTask.data ? serviceTask.data : serviceTask)?.breakdown_report ||
            (serviceTask.data ? serviceTask.data : serviceTask)?.breakdownReport
        ),
        noHistory: true,
        lastCompleted: '',
        lastCompletedOdometerKm: null,
        lastCompletedEngineHours: null,
        lastCompletedImplementUsageHours: null,
        lastCompletedReviewedBy: null,
        lastCompletedTimeDiffDays: 0,
        timeDiffDays: null,
        odometerDiffKm: null,
        engineHoursDiff: null,
        implementUsageHoursDiff: null,
        currentOdometerKm: null,
        currentEngineHours: null,
        currentImplementUsageHours: null,
        machineType: null,
      };

      if (idx >= 0) {
        state.maintenanceItems[idx] = {...state.maintenanceItems[idx], activeServiceEntry: entry.data || entry};
      } else {
        state.maintenanceItems.push({...syntheticRow, activeServiceEntry: entry.data || entry});
      }
    },
    /**
     * Merge an updated service entry into historyItems so the History table
     * updates immediately without a full data refetch.
     * @param {Object} state - Current shopview slice state
     * @param {Object} action - payload: { entryId, updatedData }
     */
    mergeHistoryEntry(state, action) {
      const {entryId, updatedData} = action.payload;
      if (!entryId || !updatedData) return;

      // Parse if it's a JSON string (backend may return stringified data)
      const parsedData = typeof updatedData === 'string' ? JSON.parse(updatedData) : updatedData;

      const idx = state.historyItems.findIndex((item) => {
        const itemId = item.serviceEntry?.id ?? item.serviceEntry?.data?.id;
        return String(itemId) === String(entryId);
      });

      if (idx >= 0) {
        state.historyItems[idx] = {
          ...state.historyItems[idx],
          serviceEntry: {
            ...state.historyItems[idx].serviceEntry,
            ...parsedData,
          },
        };
      }
    },
  },
});

function getServiceData() {
  return async (dispatch, getState) => {
    dispatch(shopviewSlice.actions.updateLoading(true));

    const getMaintenanceDataRequest = fetch('/shopview/getMaintenanceData', {cache: 'no-store'});

    // Get vehicle information
    const getVehiclesRequest = fetch('/shopview/getVehicles', {cache: 'no-store'});
    const getVehicleLabelsRequest = fetch('/getVehicleLabels', {cache: 'no-store'});

    // Get inspection items
    const getInspectionItemsRequest = fetch('/getInspectionItems', {cache: 'no-store'});

    // Get Service Checklist items
    const getServiceChecklistItemsRequest = fetch('/getServiceChecklistItems', {cache: 'no-store'});

    // Get implement information
    const getImplementsRequest = fetch('/getImplements', {cache: 'no-store'});

    // Get Implement Labels
    const getImplementLabelsRequest = fetch('/settings/implementLabels', {cache: 'no-store'});

    // Get breakdown reports
    const getBreakdownReportsRequest = fetch('/getBreakdownReports', {cache: 'no-store'});

    // Make all calls
    const [
      getMaintenanceDataResponse,
      getVehiclesResponse,
      getVehicleLabelsResponse,
      getInspectionItemsResponse,
      getServiceChecklistItemsResponse,
      getImplementsResponse,
      getImplementLabelsResponse,
      getBreakdownReportsResponse,
    ] = await Promise.all([
      getMaintenanceDataRequest,
      getVehiclesRequest,
      getVehicleLabelsRequest,
      getInspectionItemsRequest,
      getServiceChecklistItemsRequest,
      getImplementsRequest,
      getImplementLabelsRequest,
      getBreakdownReportsRequest,
    ]);

    // Get maintenance data
    const maintenanceData = await getMaintenanceDataResponse.json();

    // Get vehicles collection
    const vehicles = await getVehiclesResponse.json();
    const vehicleLabels = await getVehicleLabelsResponse.json();

    // Get service tasks collections
    const serviceTasks = maintenanceData.serviceTaskDocs;

    // Get Inspection Items
    const inspectionItems = await getInspectionItemsResponse.json();

    // Get Service Checklist Items
    const serviceChecklistItems = await getServiceChecklistItemsResponse.json();

    // Get implements and implement labels
    const implementsData = await getImplementsResponse.json();
    const implementLabelsData = await getImplementLabelsResponse.json();
    const implementLabels = implementLabelsData.data || [];

    const breakdownReports = await getBreakdownReportsResponse.json();

    dispatch(
      shopviewSlice.actions.updateShopviewData({
        vehicles,
        vehicleLabels,
        serviceTasks,
        inspectionItems,
        serviceChecklistItems,
        implementLabels,
        breakdownReports,
      })
    );
    dispatch(shopviewSlice.actions.updateMaintenanceData(maintenanceData));
    dispatch(
      shopviewSlice.actions.updateImplementData({
        implements: implementsData,
      })
    );
    dispatch(shopviewSlice.actions.updateLoading(false));

    // Lazily load each inspection-failure task's newest open linked report after the page data is up,
    // so banners are built synchronously from store data instead of per-modal-open fetches
    const newestOpenReportIds = new Set();
    for (const serviceTask of serviceTasks || []) {
      if (!serviceTask.inspection_failure) {
        continue;
      }
      const openReportIds = getOpenInspectionReportIds(serviceTask, maintenanceData.historyItems);
      if (openReportIds.length > 0) {
        newestOpenReportIds.add(openReportIds[openReportIds.length - 1]);
      }
    }
    if (newestOpenReportIds.size > 0) {
      try {
        const reportsResponse = await fetch(
          '/getInspectionReportsByIds?' + new URLSearchParams({ids: [...newestOpenReportIds].join(',')}),
          {cache: 'no-store'}
        );
        const reports = await reportsResponse.json();
        const reportsById = {};
        for (const report of reports) {
          reportsById[report.id] = report;
        }
        dispatch(shopviewSlice.actions.updateLinkedInspectionReports(reportsById));
      } catch (error) {
        // banners fall back to the stored task notes when the reports are unavailable
      }
    }
  };
}

export {getServiceData};
export const {
  updateAddHistoryModalOpen,
  updateUseCompleteTaskDefaultValues,
  mergeActiveServiceEntry,
  mergeHistoryEntry,
  updateAlertviewLookups,
} = shopviewSlice.actions;
export default shopviewSlice.reducer;
